/**
 * ModuleLocked — what a tenant sees on a page of a module it has not bought.
 *
 * The app chrome (sidebar, header) stays, so the reader can keep browsing; the
 * content area shows a neutral, blurred page skeleton under a card that says
 * the module isn't in the plan and who to ask.
 *
 * THE OVERLAY IS PRESENTATION, NOT THE GUARANTEE. Nothing here can be "removed"
 * to reveal data, because there is none: the real page component is never
 * mounted when its module is locked (ProtectedRoute / ModuleLockBoundary render
 * this instead), so it never fetches, and every service answers an unlicensed
 * org with 403 module_not_licensed anyway. The skeleton is grey blocks only —
 * no numbers, no text, nothing fabricated.
 *
 * There is deliberately no close button, Escape does nothing, and pointer
 * events on the blurred area are swallowed.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../common/FeatherIcon';
import useModuleLicense from '../../licensing/useModuleLicense';
import { fetchOrgAdmins } from './orgAdmins';
import './ModuleLocked.css';

const MAX_ADMINS = 3;

const swallow = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

/** Grey placeholder blocks in the shape of a typical page. No content. */
const SkeletonPage = () => (
  <div className="module-locked__skeleton" aria-hidden="true" inert>
    <div className="ml-sk ml-sk--title" />
    <div className="ml-sk ml-sk--subtitle" />
    <div className="ml-sk-row">
      {[0, 1, 2, 3].map((i) => <div key={i} className="ml-sk ml-sk--tile" />)}
    </div>
    <div className="ml-sk-row">
      <div className="ml-sk ml-sk--panel ml-sk--wide" />
      <div className="ml-sk ml-sk--panel" />
    </div>
    <div className="ml-sk ml-sk--table" />
  </div>
);

export default function ModuleLocked({ module }) {
  const { t } = useTranslation('moduleLicensing');
  const license = useModuleLicense();
  const titleId = useId();
  const headingRef = useRef(null);
  const [admins, setAdmins] = useState([]);

  const label = license.moduleLabel(module) || t('moduleLicensing:lock.fallbackModule', { defaultValue: 'This module' });

  useEffect(() => {
    let live = true;
    fetchOrgAdmins()
      .then((list) => { if (live) setAdmins(list.slice(0, MAX_ADMINS)); })
      .catch(() => { if (live) setAdmins([]); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    // Move focus to the heading so a screen reader announces why the page is
    // empty; preventScroll keeps the sidebar where the reader left it.
    try { headingRef.current?.focus({ preventScroll: true }); } catch { /* old browsers */ }
  }, [module]);

  // Escape never dismisses this screen — and must not reach a handler further
  // up that might close something around it.
  const onKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') swallow(e);
  };

  return (
    // role="region" is explicit on purpose: a <section> is only a landmark
    // when it is named, and assistive tech must always find this one.
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <section
      className="module-locked"
      role="region"
      aria-labelledby={titleId}
      data-testid="module-locked"
      data-module={module || ''}
      onKeyDown={onKeyDown}
    >
      <SkeletonPage />
      {/* Covers the skeleton and eats every pointer event aimed at it. */}
      <div
        className="module-locked__scrim"
        aria-hidden="true"
        data-testid="module-locked-scrim"
        onClickCapture={swallow}
        onMouseDownCapture={swallow}
        onPointerDownCapture={swallow}
        onContextMenu={swallow}
      />
      <div className="module-locked__card">
        <span className="module-locked__icon" aria-hidden="true">
          <FeatherIcon name="lock" size={22} />
        </span>
        <h2 id={titleId} ref={headingRef} tabIndex={-1} className="module-locked__title">
          {t('moduleLicensing:lock.title', {
            module: label,
            defaultValue: "{{module}} isn't included in your plan",
          })}
        </h2>
        <p className="module-locked__body">
          {t('moduleLicensing:lock.body', {
            defaultValue: 'To get access to this page, please contact your administrator.',
          })}
        </p>
        {admins.length > 0 && (
          <div className="module-locked__admins" data-testid="module-locked-admins">
            <h3 className="module-locked__admins-heading">
              {t('moduleLicensing:lock.adminsHeading', { defaultValue: 'Your administrators' })}
            </h3>
            <ul>
              {admins.map((a) => (
                <li key={a.email}>
                  {a.name && <span className="module-locked__admin-name">{a.name}</span>}
                  <a href={`mailto:${a.email}`} className="module-locked__admin-email">{a.email}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
